import type { SimulationFrame } from '@rcs/physics';

/** グラフ用の系列（距離を横軸にした 3 本）。 */
export interface RideSeries {
  /** 走行距離 [m] */
  readonly distance: readonly number[];
  /** 速度 [km/h] */
  readonly speedKmh: readonly number[];
  /** 高度 [m] */
  readonly altitude: readonly number[];
  /** 上下 G */
  readonly verticalG: readonly number[];
  /** 各点の経過時間 [s]（再生位置から距離を引くのに使う） */
  readonly time: readonly number[];
}

/**
 * 結果のフレームから、グラフの系列を作る。点が `maxPoints` を超えるときは等間隔に間引く
 * （最初と最後の点は必ず残す）。
 */
export function buildSeries(frames: readonly SimulationFrame[], maxPoints = 400): RideSeries {
  const count = frames.length;
  const step = count <= maxPoints ? 1 : (count - 1) / (maxPoints - 1);
  const distance: number[] = [];
  const speedKmh: number[] = [];
  const altitude: number[] = [];
  const verticalG: number[] = [];
  const time: number[] = [];
  const total = count <= maxPoints ? count : maxPoints;
  for (let i = 0; i < total; i += 1) {
    const frame = frames[Math.min(count - 1, Math.round(i * step))];
    if (!frame) continue;
    distance.push(frame.s);
    speedKmh.push(frame.speed * 3.6);
    altitude.push(frame.position[1]);
    verticalG.push(frame.gForce.vertical);
    time.push(frame.time);
  }
  return { distance, speedKmh, altitude, verticalG, time };
}

/** 再生時刻 `time` に最も近い点の、系列内の番号（時刻は昇順）。 */
export function indexAtTime(series: RideSeries, time: number): number {
  const times = series.time;
  if (times.length === 0) return 0;
  let low = 0;
  let high = times.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if ((times[mid] ?? 0) < time) low = mid + 1;
    else high = mid;
  }
  const previous = Math.max(0, low - 1);
  return Math.abs((times[previous] ?? 0) - time) <= Math.abs((times[low] ?? 0) - time)
    ? previous
    : low;
}
